import { ZusAggregatedProfile } from '@ctw/shared/content/zus-aggregated-profile';
import { CTWProvider } from '@ctw/shared/context/ctw-context';
import { PatientProvider } from '@ctw/shared/context/patient-provider';
import type { ZapIFrameConfig } from '@ctw/shared/types/zap-message';
import { getAuthTokenState } from '@ctw/shared/utils/auth';
import { useMemo } from 'react';
import {
  SalesforceEnvironmentProvider,
  useSalesforceEnvironment,
} from '../salesforce-context/salesforce-environment-context';
import { SalesforceUserProvider } from '../salesforce-context/salesforce-user-context';
import { useZusToken, ZusTokenProvider } from '../salesforce-context/zus-token-context';

function SalesforceV1Content({ zapConfig }: { zapConfig: Partial<ZapIFrameConfig> | null }) {
  const salesforceEnvironment = useSalesforceEnvironment();
  const zusToken = useZusToken();

  if (!zusToken.zusAccessToken) {
    throw new Error('Missing Zus access token');
  }

  if (!salesforceEnvironment.environment?.accountId) {
    throw new Error('Missing Salesforce context');
  }

  const authTokenState = getAuthTokenState(zusToken.zusAccessToken);

  return (
    <CTWProvider
      service="zap"
      serviceVersion={import.meta.env.VITE_GIT_SHA}
      serviceVariant="salesforce-v1"
      builderId={authTokenState.builderId}
      authToken={zusToken.zusAccessToken}
      ehr="salesforce"
      devProps={zapConfig?.devProps}
    >
      <PatientProvider
        systemURL="https://salesforce.com/account-id"
        patientID={salesforceEnvironment.environment.accountId}
      >
        <ZusAggregatedProfile />
      </PatientProvider>
    </CTWProvider>
  );
}

export function SalesforceV1() {
  const zapConfig: Partial<ZapIFrameConfig> | null = useMemo(
    () =>
      JSON.parse(
        new URLSearchParams(window.location.search).get('config') ?? 'null',
      ) as null | Partial<ZapIFrameConfig>,
    [],
  );

  return (
    <SalesforceUserProvider>
      <SalesforceEnvironmentProvider>
        <ZusTokenProvider zapConfig={zapConfig}>
          <SalesforceV1Content zapConfig={zapConfig} />
        </ZusTokenProvider>
      </SalesforceEnvironmentProvider>
    </SalesforceUserProvider>
  );
}
